const BILLING_ALLOWED_STATUSES = new Set(['active', 'trialing']);
const PLAN_FULL_ACCESS = new Set(['pro', 'enterprise']);
const PLAN_LIMITED_ACCESS = new Set(['essencial']);
const PLAN_RESTRICTED_PRODUCTS = new Set(['atendabot', 'smart', 'cv']);
const TRIAL_INCLUDED_PRODUCTS = new Set([
  'checklist',
  'reservo',
  'clube_mesa',
  'cv',
  'cardapio',
  'feedback_hero',
  'crm',
  'linkpage',
]);
const TRIAL_RESTRICTED_PRODUCT_ENTRY = {
  atendabot: '/atendabot/prompt',
};
const TRIAL_CONTRACTABLE_PRODUCTS = new Set(['atendabot']);

const getAdminAccess = (source) =>
  source?.user?.isAdmin === true || source?.isAdmin === true;

const getProductContainer = (source) => {
  if (source?.products && typeof source.products === 'object') {
    return source.products;
  }

  if (source?.produtos && typeof source.produtos === 'object') {
    return source.produtos;
  }

  return {};
};

const getExplicitProductAccess = (source, productKey) => {
  if (!source || !productKey) return null;

  const productContainer = getProductContainer(source);

  if (
    productContainer
    && Object.prototype.hasOwnProperty.call(productContainer, productKey)
  ) {
    return Boolean(productContainer[productKey]);
  }

  if (Object.prototype.hasOwnProperty.call(source, productKey)) {
    return Boolean(source[productKey]);
  }

  return null;
};

const hasInheritedBilling = (source) => (
  source?.billing?.inherited === true
  || source?.billing_inherited === true
);

export const getBillingSnapshot = (source = {}) => {
  const billing = source?.billing || {};
  return {
    status: billing.status || source.subscription_status || null,
    plan: billing.plan || source.subscription_plan || source.plan_key || null,
    promotionAccessTrialEndsAt:
      billing.promotionAccessTrialEndsAt
      || billing.promotion_access_trial_ends_at
      || source.promotionAccessTrialEndsAt
      || source.promotion_access_trial_ends_at
      || null,
    billingExempt:
      billing.billingExempt !== undefined
        ? Boolean(billing.billingExempt)
        : billing.billing_exempt !== undefined
          ? Boolean(billing.billing_exempt)
          : Boolean(source.billing_exempt),
  };
};

export const hasActivePromotionAccessTrial = (source, now = new Date()) => {
  const { promotionAccessTrialEndsAt, billingExempt } = getBillingSnapshot(source);
  if (billingExempt || !promotionAccessTrialEndsAt) return false;
  const expiresAt = new Date(promotionAccessTrialEndsAt);
  return !Number.isNaN(expiresAt.getTime()) && expiresAt > now;
};

export const hasActiveAtendabotWhatsappTrial = (source, now = new Date()) => {
  const value = source?.productTrials?.atendabotEndsAt
    || source?.atendabot_whatsapp_trial_expires_at
    || null;
  if (!value) return false;
  const expiresAt = new Date(value);
  return !Number.isNaN(expiresAt.getTime()) && expiresAt > now;
};

export const hasBillingAccess = (source) => {
  if (!source) return false;
  if (getAdminAccess(source)) return true;
  const { status, billingExempt } = getBillingSnapshot(source);
  if (billingExempt) return true;
  if (!status) return false;
  return BILLING_ALLOWED_STATUSES.has(String(status).trim().toLowerCase());
};

export const hasPlanAccess = (source, productKey) => {
  if (!source) return false;
  if (!productKey) return true;
  if (getAdminAccess(source)) return true;

  const { status, plan, billingExempt } = getBillingSnapshot(source);
  if (billingExempt) return true;

  const normalizedPlan = plan ? String(plan).trim().toLowerCase() : null;
  const normalizedStatus = status ? String(status).trim().toLowerCase() : null;
  if (!normalizedStatus || !BILLING_ALLOWED_STATUSES.has(normalizedStatus)) {
    return false;
  }
  const isTrial = normalizedStatus === 'trialing';

  if (isTrial) {
    return TRIAL_INCLUDED_PRODUCTS.has(productKey);
  }

  if (PLAN_FULL_ACCESS.has(normalizedPlan)) {
    return true;
  }

  if (PLAN_LIMITED_ACCESS.has(normalizedPlan)) {
    return !PLAN_RESTRICTED_PRODUCTS.has(productKey);
  }

  return Boolean(getProductContainer(source)?.[productKey]);
};

export const hasProductAccess = (source, productKey, options = {}) => {
  const { allowTrialRestricted = false } = options;

  if (!source) return false;
  if (!productKey) return true;
  if (getAdminAccess(source)) return true;

  if (productKey === 'atendabot' && hasActiveAtendabotWhatsappTrial(source)) {
    return true;
  }

  const explicitAccess = getExplicitProductAccess(source, productKey);

  if (allowTrialRestricted && hasTrialRestrictedProductAccess(source, productKey)) {
    return true;
  }

  if (explicitAccess === true && hasTrialContractedProductAccess(source, productKey)) {
    return true;
  }

  if (hasTrialPlanProductAccess(source, productKey)) {
    return true;
  }

  if (explicitAccess === false) {
    return false;
  }

  if (hasPlanAccess(source, productKey)) {
    return true;
  }

  return false;
};

export const hasTrialRestrictedProductAccess = (source, productKey) => {
  if (!source || !productKey) return false;
  if (getAdminAccess(source)) return false;
  if (productKey === 'atendabot' && hasActiveAtendabotWhatsappTrial(source)) return false;
  const explicitAccess = getExplicitProductAccess(source, productKey);
  const inheritsBilling = hasInheritedBilling(source);
  if (inheritsBilling && explicitAccess !== true) return false;
  if (!inheritsBilling && explicitAccess === true) return false;

  const { status, billingExempt } = getBillingSnapshot(source);
  if (billingExempt) return false;

  const normalizedStatus = status ? String(status).trim().toLowerCase() : null;
  if (normalizedStatus !== 'trialing' && !hasActivePromotionAccessTrial(source)) {
    return false;
  }

  return Boolean(TRIAL_RESTRICTED_PRODUCT_ENTRY[productKey]);
};

const hasTrialContractedProductAccess = (source, productKey) => {
  if (!source || !productKey) return false;
  if (getAdminAccess(source)) return false;
  if (hasInheritedBilling(source)) return false;
  if (!TRIAL_CONTRACTABLE_PRODUCTS.has(productKey)) return false;

  const { status, billingExempt } = getBillingSnapshot(source);
  if (billingExempt) return false;

  const normalizedStatus = status ? String(status).trim().toLowerCase() : null;
  return normalizedStatus === 'trialing';
};

const hasTrialPlanProductAccess = (source, productKey) => {
  if (!source || !productKey) return false;
  if (getAdminAccess(source)) return false;

  if (
    hasInheritedBilling(source)
    && getExplicitProductAccess(source, productKey) !== true
  ) {
    return false;
  }

  const { status, billingExempt } = getBillingSnapshot(source);
  if (billingExempt) return false;

  const normalizedStatus = status ? String(status).trim().toLowerCase() : null;
  if (normalizedStatus !== 'trialing' && !hasActivePromotionAccessTrial(source)) {
    return false;
  }

  return TRIAL_INCLUDED_PRODUCTS.has(productKey);
};

export const hasPromptOnlyProductAccess = (source, productKey) =>
  hasTrialRestrictedProductAccess(source, productKey);

export const getProductEntryPath = (source, productKey, defaultPath) => {
  if (
    hasPromptOnlyProductAccess(source, productKey)
    && hasProductAccess(source, productKey, { allowTrialRestricted: true })
  ) {
    return TRIAL_RESTRICTED_PRODUCT_ENTRY[productKey] || defaultPath;
  }

  return defaultPath;
};
